import { Alert } from '@mui/material';
import React from 'react';

import { ClientLaunchType } from '../../../graphql/types';
import { BasicsStep } from './BasicsStep';
import { REVIEW_STEP_ID } from './launchState';
import { ReviewStep } from './ReviewStep';
import type { CheckpointItem, LaunchStep } from './types';

// Apart from Client basics and Review, steps are placeholders: each flow
// ticket replaces one with its real form. Their
// checkpoint items come from the mockup and are still being reviewed.

const PlaceholderStep: React.FC = () => (
  <Alert severity="info">This step's fields aren't built yet.</Alert>
);

const noCheckpoint = () => [];

const studiosCheckpoint = (): CheckpointItem[] => [
  {
    id: 'site',
    label: 'Each MBO site ID was checked against the site in Mindbody',
    help: 'There is no Mindbody sandbox. A wrong site ID points members at a real, unrelated studio.',
  },
  {
    id: 'api',
    label: 'Each studio has been authenticated to be used with the MBO API',
  },
  {
    id: 'tz',
    label: 'Studio time zones match the physical gyms',
    help: 'Class times in the app are shown in the studio time zone.',
  },
];

const featuresCheckpoint = (): CheckpointItem[] => [
  {
    id: 'feat',
    label:
      'I have reviewed this selection of features and confirm it is correct',
  },
];

const featureNames: LaunchStep['summary'] = (launch) =>
  launch.features.map((f) => f.name).join(', ') || 'None';

const basics: LaunchStep = {
  id: 'basics',
  title: 'Client basics',
  subtitle: 'Name and logo',
  intro: 'Continuing creates the client. Each later step saves as you go.',
  fieldNames: ['clients'],
  checkpoint: noCheckpoint,
  summary: (launch) => launch.client.name ?? '',
  Component: BasicsStep,
};

const eligibility: LaunchStep = {
  id: 'elig',
  title: 'Eligibility',
  subtitle: 'Who gets access',
  intro: 'Who can sign up as a member of this client.',
  fieldNames: ['clients', 'eligible_email_addresses', 'domains'],
  checkpoint: () => [
    {
      id: 'sftp',
      label: 'Client has an SFTP account and has the file format',
      help: 'A CSV with email addresses in the first column. Office ID and Employee Type columns are optional.',
    },
  ],
  Component: PlaceholderStep,
};

const signIn: LaunchStep = {
  id: 'signin',
  title: 'Sign-in method',
  subtitle: 'SSO or password',
  fieldNames: ['clients'],
  checkpoint: () => [
    {
      id: 'sso',
      label: "Engineering has configured the client's Auth0 connection",
    },
    {
      id: 'reg',
      label: 'An eligible user was able to complete registration for the app',
      help: 'Checks sign-in and eligibility together. Use a test user from the Eligibility step.',
    },
  ],
  Component: PlaceholderStep,
};

const studios: LaunchStep = {
  id: 'studios',
  title: 'Studios & locations',
  subtitle: 'Gyms in Mindbody',
  intro:
    'Each studio is one Mindbody site. Members join a gym at one of its locations.',
  fieldNames: ['studios', 'locations', 'mbo_contract_mappings'],
  checkpoint: studiosCheckpoint,
  Component: PlaceholderStep,
};

const features: LaunchStep = {
  id: 'features',
  title: 'Features',
  subtitle: 'What members get',
  fieldNames: ['clients', 'feature_grants'],
  checkpoint: featuresCheckpoint,
  summary: featureNames,
  Component: PlaceholderStep,
};

const coaches: LaunchStep = {
  id: 'coaches',
  title: 'Coaches',
  subtitle: 'Who serves members',
  fieldNames: ['coach_client'],
  checkpoint: noCheckpoint,
  Component: PlaceholderStep,
};

const review: LaunchStep = {
  id: REVIEW_STEP_ID,
  title: 'Review & finish',
  subtitle: 'Final sign-off',
  intro: 'Everything below is saved.',
  checkpoint: () => [
    {
      id: 'diag',
      label: 'Client diagnostics ran with no issues',
      help: 'Ticks itself when the latest run is clean. Run it again after any change.',
      isAutoDone: ({ diagnosticsClean }) => diagnosticsClean,
    },
    { id: 'second', label: 'A second person reviewed this setup' },
    {
      id: 'launch',
      label:
        'Client contact knows the launch date and member communications are ready',
    },
  ],
  Component: ReviewStep,
};

const NEW_CLIENT_STEPS = [
  basics,
  eligibility,
  signIn,
  studios,
  features,
  coaches,
  review,
];

const FEATURE_LAUNCH_STEPS: LaunchStep[] = [
  {
    ...studios,
    title: 'Review studio configuration',
    subtitle: 'Mindbody readiness',
    intro: 'Features need every studio set up and authenticated in Mindbody.',
  },
  {
    ...features,
    subtitle: 'Test users, then everyone',
    fieldNames: ['feature_grants', 'eligible_email_addresses'],
  },
  review,
];

export const getLaunchSteps = (type: ClientLaunchType) =>
  type === ClientLaunchType.FeatureLaunch
    ? FEATURE_LAUNCH_STEPS
    : NEW_CLIENT_STEPS;
